import { createSignal, onCleanup } from 'solid-js'

/**
 * Selección y redimensionado de las imágenes del editor.
 *
 * El navegador no ofrece manijas propias en `contentEditable` (Chrome las quitó
 * hace años), así que se pintan aquí: al hacer clic en una imagen se marca como
 * seleccionada y se dibuja un marco con cuatro tiradores en las esquinas.
 * Arrastrar cualquiera cambia el tamaño conservando la proporción.
 *
 * La medida se escribe como **atributos** `width`/`height` y como `style`
 * (`width` en px): los tres están en la política de saneado, así que el tamaño
 * sobrevive al guardar. Con `style` solo, un correo o un PDF que ignore CSS
 * pintaría la imagen a tamaño completo.
 */
export interface ImageBox {
  left: number
  top: number
  width: number
  height: number
}

export type ResizeHandle = 'nw' | 'ne' | 'sw' | 'se'

export function useImageResize(getEditor: () => HTMLElement | undefined, onChange: () => void) {
  const [selected, setSelected] = createSignal<HTMLImageElement | null>(null)
  const [box, setBox] = createSignal<ImageBox | null>(null)

  /** Recalcula el marco a partir de la posición real de la imagen. */
  const measure = () => {
    const img = selected()
    const editor = getEditor()
    if (!img || !editor?.contains(img)) {
      setBox(null)
      return
    }
    const imgRect = img.getBoundingClientRect()
    const hostRect = editor.getBoundingClientRect()
    setBox({
      left: imgRect.left - hostRect.left + editor.scrollLeft,
      top: imgRect.top - hostRect.top + editor.scrollTop,
      width: imgRect.width,
      height: imgRect.height
    })
  }

  const select = (img: HTMLImageElement | null) => {
    setSelected(img)
    if (img) requestAnimationFrame(measure)
    else setBox(null)
  }

  /** Fija el tamaño en atributos y en style, para que viaje a cualquier sitio. */
  const applySize = (img: HTMLImageElement, width: number, height: number) => {
    const w = Math.max(24, Math.round(width))
    const h = Math.max(16, Math.round(height))
    img.setAttribute('width', String(w))
    img.setAttribute('height', String(h))
    img.style.width = `${w}px`
    img.style.height = 'auto'
    measure()
  }

  /** Ancho en porcentaje del contenedor (los botones 25/50/100 %). */
  const setRelativeWidth = (percent: number) => {
    const img = selected()
    const editor = getEditor()
    if (!img || !editor) return
    const available = editor.clientWidth - 32
    const width = (available * percent) / 100
    const ratio = img.naturalHeight && img.naturalWidth ? img.naturalHeight / img.naturalWidth : 0.66
    applySize(img, width, width * ratio)
    onChange()
  }

  /** Alineación de la imagen dentro del texto. */
  const setAlignment = (align: 'left' | 'center' | 'right') => {
    const img = selected()
    if (!img) return
    img.style.float = align === 'center' ? '' : align
    img.style.marginLeft = align === 'center' || align === 'right' ? 'auto' : ''
    img.style.marginRight = align === 'center' || align === 'left' ? 'auto' : ''
    img.style.display = align === 'center' ? 'block' : ''
    measure()
    onChange()
  }

  const remove = () => {
    const img = selected()
    if (!img) return
    img.remove()
    select(null)
    onChange()
  }

  let dragging: { handle: ResizeHandle; startX: number; startWidth: number; ratio: number; img: HTMLImageElement } | null = null

  const onPointerMove = (event: PointerEvent) => {
    if (!dragging) return
    const delta = event.clientX - dragging.startX
    // Las manijas de la izquierda crecen hacia el otro lado.
    const signed = dragging.handle === 'nw' || dragging.handle === 'sw' ? -delta : delta
    const width = dragging.startWidth + signed
    applySize(dragging.img, width, width * dragging.ratio)
  }

  const onPointerUp = () => {
    if (!dragging) return
    dragging = null
    window.removeEventListener('pointermove', onPointerMove)
    window.removeEventListener('pointerup', onPointerUp)
    onChange()
  }

  const startResize = (event: PointerEvent, handle: ResizeHandle) => {
    const img = selected()
    if (!img) return
    event.preventDefault()
    event.stopPropagation()
    const rect = img.getBoundingClientRect()
    dragging = {
      handle,
      startX: event.clientX,
      startWidth: rect.width,
      ratio: rect.height / rect.width,
      img
    }
    window.addEventListener('pointermove', onPointerMove)
    window.addEventListener('pointerup', onPointerUp)
  }

  onCleanup(() => {
    window.removeEventListener('pointermove', onPointerMove)
    window.removeEventListener('pointerup', onPointerUp)
  })

  return { selected, box, select, measure, startResize, setRelativeWidth, setAlignment, remove }
}
